import React, { FunctionComponent } from 'react'
import AppBar from '@material-ui/core/AppBar'
import CssBaseline from '@material-ui/core/CssBaseline'
import Divider from '@material-ui/core/Divider'
import Drawer from '@material-ui/core/Drawer'
import Hidden from '@material-ui/core/Hidden'
import IconButton from '@material-ui/core/IconButton'
import { useHistory } from 'react-router-dom'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MenuIcon from '@material-ui/icons/Menu'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import MenuItem from '@material-ui/core/MenuItem'
import Menu from '@material-ui/core/Menu'
import AccountCircle from '@material-ui/icons/AccountCircle'
import Button from '@material-ui/core/Button'
import LogoutIcon from 'mdi-material-ui/Logout'
import LanguageIcon from '@material-ui/icons/Language'
import PersonIcon from '@material-ui/icons/Person'
import {
  makeStyles,
  useTheme,
  Theme,
  createStyles,
} from '@material-ui/core/styles'
import ParkingIcon from 'mdi-material-ui/Parking'
import TicketIcon from 'mdi-material-ui/Ticket'
import ReservationsIcon from 'Elements/ReservationsIcon'
import { useTranslation } from 'react-i18next'
import { MobillyLogo } from 'Elements/MobillyLogo'
import clsx from 'clsx'
import {
  myParkingLotPermission,
  myParkingLotPaymentsPermission,
  myParkingLotAnalyticsPermission,
  myParkingLotParkingLotPermission,
  myParkingLotCustomerNotificationExclusionsPermission,
  subscriptionsPermission,
  subscriptionsFreeTimePermission,
  subscriptionsSubscriptionsPermission,
  subscriptionsPermitsPermission,
  subscriptionsSubscriptionOffersPermission,
  reservationsPermission,
  reservationsMyReservationsPermission,
  reservationsReservationsPermission,
  reservationsReservationOffersPermission,
} from 'utils'
import { usePermissions } from 'components/providers/PermissionProvider'
import { ROUTE } from 'constants/Routes'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import { useMediaQuery } from '@material-ui/core'
import {useApi} from "components/providers/ApiProvider";
import { useProfile } from 'components/providers/ProfileProvider'
import Box from '@material-ui/core/Box'
import Skeleton from '@material-ui/lab/Skeleton'

const drawerWidth = 240

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      height: '100%',
    },

    hide: {
      display: 'none',
    },
    grow: {
      flexGrow: 1,
    },

    appBar: {},
    appBarShift: {
      [theme.breakpoints.up('sm')]: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: drawerWidth,
      },
    },

    drawerOpenButton: {
      [theme.breakpoints.up('sm')]: {
        marginRight: 12,
      },
    },

    mainMenu: {
      position: 'relative',
      marginRight: theme.spacing(2),
      marginLeft: 0,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        marginRight: 0,
      },
    },
    mainMenuButton: {
      marginRight: '5px',
      [theme.breakpoints.down('sm')]: {
        marginRight: '0px',
        width: '100%',
        padding: '6px',
      },
    },
    mainMenuLabel: {
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
    mainMenuStartIcon: {
      [theme.breakpoints.down('sm')]: {
        margin: 0,
      },
    },
    mainMenuTitle: {
      fontSize: '15px',
      letterSpacing: '1px',
      [theme.breakpoints.down('sm')]: {
        fontSize: '10px',
        marginTop: '3px',
        lineHeight: '15px',
      },
    },

    language: {
      minWidth: 'auto',
      padding: '4px 8px',
      fontSize: theme.typography.pxToRem(14),
    },

    drawer: {
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
        flexShrink: 0,
      },
    },
    drawerPaper: {
      width: drawerWidth,
      color: 'white',
    },

    drawerMenuItem: {
      borderRadius: '8px',
      color: '#F7F7F7',
        '&:hover': {
          backgroundColor: 'rgba(64, 60, 57, 0.5)', // #403C39
        }
    },
    drawerMenuItemSelected: {
      backgroundColor: '#403C39 !important',
      color: '#F37121',
    },

    content: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.up('sm')]: {
        marginLeft: -drawerWidth,
      },
    },
    contentShift: {
      [theme.breakpoints.up('sm')]: {
        marginLeft: 0,
      },
    },
    toolbar: {
      ...theme.mixins.toolbar,
      minHeight: 64,
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '15px',
    },
    mainWrap: {
      flexGrow: 1,
    },
  }),
)

interface Props {
}

export const MainNavigation: FunctionComponent<Props> = ({ children }) => {
  const { t, i18n } = useTranslation()
  const profile = useProfile()
  const permissions = usePermissions()
  const history = useHistory()
  const classes = useStyles()
  const theme = useTheme()
  const api = useApi();

  const [temporaryDrawerOpen, setTemporaryDrawerOpen] = React.useState(false)
  const [persistentDrawerOpen, setPersistentDrawerOpen] = React.useState(sessionStorage.getItem('drawer') != 'hidden')
  const [accountMenuAnchorEl, setAccountMenuAnchorEl] = React.useState<null | HTMLElement>(null)

  const isXSmallScreen = !useMediaQuery(theme.breakpoints.up('sm'))
  const accountMenuOpen = Boolean(accountMenuAnchorEl)

  const currentMainPath = `/${history.location.pathname.split('/')[1]}`

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchorEl(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountMenuAnchorEl(null)
  }

  const handleDrawerOpen = () => {
    setTemporaryDrawerOpen(true)
    setPersistentDrawerOpen(true)
    sessionStorage.removeItem('drawer')
  }

  const handleDrawerClose = () => {
    setTemporaryDrawerOpen(false)
    setPersistentDrawerOpen(false)
    sessionStorage.setItem('drawer', 'hidden')
  }

  const LANGUAGES = ['lv', 'ru', 'en']

  const TOP_NAVIGATION = [
    ...(myParkingLotPermission(permissions)
      ? [
        {
          title: t('parking'),
          icon: <ParkingIcon />,
          path: ROUTE.MY_PARKING_LOT.MAIN,
          id: ROUTE.MY_PARKING_LOT.MAIN,
        },
      ]
      : []),
    ...(subscriptionsPermission(permissions)
      ? [
        {
          title: t('subscriptions'),
          icon: <TicketIcon />,
          path: ROUTE.SUBSCRIPTIONS.MAIN,
          id: ROUTE.SUBSCRIPTIONS.MAIN,
        },
      ]
      : []),
    ...(reservationsPermission(permissions)
      ? [
        {
          title: t('reservations'),
          icon: <ReservationsIcon />,
          path: ROUTE.RESERVATIONS.MAIN,
          id: ROUTE.RESERVATIONS.MAIN,
        },
      ]
      : []),
  ]

  const SUBSCRIPTIONS_SIDE_NAV = [
    {
      title: t('mySubscriptions'),
      id: ROUTE.SUBSCRIPTIONS.MAIN,
      path: ROUTE.SUBSCRIPTIONS.HOME,
      activePaths: [ROUTE.SUBSCRIPTIONS.HOME, ROUTE.SUBSCRIPTIONS.BUY],
    },
    ...(subscriptionsFreeTimePermission(permissions)
      ? [
        {
          title: t('addFreeTimeSubscription'),
          id: ROUTE.SUBSCRIPTIONS.MAIN,
          path: ROUTE.SUBSCRIPTIONS.FREE_TIME,
        },
      ]
      : []),
    ...(subscriptionsSubscriptionsPermission(permissions)
      ? [
        {
          title: t('subscriptions'),
          path: ROUTE.SUBSCRIPTIONS.SUBSCRIPTIONS,
          id: ROUTE.SUBSCRIPTIONS.MAIN,
        },
      ]
      : []),
    ...(subscriptionsPermitsPermission(permissions)
      ? [
        {
          title: t('permits'),
          path: ROUTE.SUBSCRIPTIONS.PERMITS,
          id: ROUTE.SUBSCRIPTIONS.MAIN,
        },
      ]
      : []),
    ...(subscriptionsSubscriptionOffersPermission(permissions)
      ? [
        {
          title: t('subscriptionOffers'),
          path: ROUTE.SUBSCRIPTIONS.SUBSCRIPTION_OFFERS,
          id: ROUTE.SUBSCRIPTIONS.MAIN,
        },
      ]
      : []),
  ]
  const PARKING_SIDE_NAV = [
    ...(myParkingLotParkingLotPermission(permissions)
      ? [
        {
          title: t('parking'),
          id: ROUTE.MY_PARKING_LOT.MAIN,
          path: ROUTE.MY_PARKING_LOT.PARKING_LOT,
        },
      ]
      : []),
    ...(myParkingLotPaymentsPermission(permissions)
      ? [
        {
          title: t('payments'),
          id: ROUTE.MY_PARKING_LOT.MAIN,
          path: ROUTE.MY_PARKING_LOT.PAYMENTS,
        },
      ]
      : []),
    ...(myParkingLotAnalyticsPermission(permissions)
      ? [
        {
          title: t('analytic'),
          id: ROUTE.MY_PARKING_LOT.MAIN,
          path: ROUTE.MY_PARKING_LOT.ANALYTICS,
        },
      ]
      : []),
    ...(myParkingLotCustomerNotificationExclusionsPermission(permissions)
      ? [
        {
          title: t('notificationExclusions'),
          id: ROUTE.MY_PARKING_LOT.MAIN,
          path: ROUTE.MY_PARKING_LOT.CUSTOMER_NOTIFICATION_EXCLUSIONS,
        },
      ]
      : []),
  ]
  const RESERVATIONS_SIDE_NAV = [
    ...(reservationsMyReservationsPermission(permissions)
      ? [
        {
          title: t('myReservations'),
          id: ROUTE.RESERVATIONS.MAIN,
          path: ROUTE.RESERVATIONS.MY_RESERVATIONS,
        },
      ]
      : []),
    ...(reservationsReservationsPermission(permissions)
      ? [
        {
          title: t('reservations'),
          id: ROUTE.RESERVATIONS.MAIN,
          path: ROUTE.RESERVATIONS.RESERVATIONS,
        },
      ]
      : []),
    ...(reservationsReservationOffersPermission(permissions)
      ? [
        {
          title: t('reservationOffers'),
          id: ROUTE.RESERVATIONS.MAIN,
          path: ROUTE.RESERVATIONS.RESERVATION_OFFERS,
        },
      ]
      : []),
  ]

  const SIDE_NAV: {
    title: string
    id: string
    path: string
    activePaths?: string[]
  }[] = [...SUBSCRIPTIONS_SIDE_NAV, ...PARKING_SIDE_NAV, ...RESERVATIONS_SIDE_NAV]

  const drawer = (
    <div style={{ paddingLeft: 10, paddingRight: 10 }}>
      <div className={classes.toolbar}>
        <Box style={{paddingTop: 10}}>
          <MobillyLogo />
        </Box>
        <Hidden xsDown>
          <IconButton size="small" style={{ color: 'white', marginLeft: 'auto' }} onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </Hidden>
      </div>
      <Divider />
      <List>
        {SIDE_NAV.filter(m => m.id === currentMainPath).map(
          ({ title, path, activePaths }, index) => {
            const selected = activePaths
              ? activePaths.find(p => history.location.pathname.includes(p))
              : history.location.pathname.includes(path)
            return (
              <ListItem
                classes={{
                  root: classes.drawerMenuItem,
                  selected: classes.drawerMenuItemSelected,
                }}
                onClick={() => {
                  if (selected) {
                    return
                  }
                  if (isXSmallScreen) {
                    handleDrawerClose()
                  }
                  history.push(path)
                }}
                button={selected ? undefined : true}
                selected={Boolean(selected)}
                key={index}
              >
                <ListItemText primary={title} />
              </ListItem>
            )
          },
        )}
      </List>
    </div>
  )

  if (!profile || !profile.username || !permissions.length) {
    return <Box display="flex" height="100vh" justifyContent="center" alignItems="center" gridColumnGap={16}>
      <Skeleton variant="circle" width={16} height={16} />
      <Skeleton variant="circle" width={16} height={16} />
      <Skeleton variant="circle" width={16} height={16} />
    </Box>
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={clsx(classes.appBar, {
        [classes.appBarShift]: persistentDrawerOpen,
      })}>
        <Toolbar style={{ minHeight: 64, padding: '6px 11px' }}>
          <Hidden smUp>
            <IconButton
              color="inherit"
              edge="start"
              className={classes.drawerOpenButton}
              onClick={handleDrawerOpen}
            >
              <MenuIcon />
            </IconButton>
          </Hidden>
          <Hidden xsDown>
            <IconButton
              color="inherit"
              className={clsx(classes.drawerOpenButton, persistentDrawerOpen && classes.hide)}
              onClick={handleDrawerOpen}
            >
              <MenuIcon />
            </IconButton>
          </Hidden>
          <div className={classes.mainMenu}>
            {TOP_NAVIGATION.map(nav => (
              <Button
                key={nav.title}
                className={classes.mainMenuButton}
                variant="contained"
                color={currentMainPath === nav.id ? 'secondary' : 'primary'}
                classes={{
                  label: classes.mainMenuLabel,
                  startIcon: classes.mainMenuStartIcon,
                }}
                startIcon={nav.icon}
                onClick={event => {
                  event.preventDefault()
                  history.push(nav.path)
                }}
              >
                <Typography className={classes.mainMenuTitle} variant="inherit">
                  {nav.title}
                </Typography>
              </Button>
            ))}
          </div>
          <div className={classes.grow} />
          <div>
            <Hidden smUp>
              <IconButton
                color="inherit"
                onClick={handleAccountMenuOpen}
              >
                <AccountCircle />
              </IconButton>
            </Hidden>
            <Hidden xsDown>
              <Button
                startIcon={<AccountCircle style={{ fontSize: 28 }} />}
                color="inherit"
                style={{ whiteSpace: 'nowrap', textTransform: 'none' }}
                onClick={handleAccountMenuOpen}
              >
                {profile.username}
              </Button>
            </Hidden>
            <Menu
              anchorEl={accountMenuAnchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={accountMenuOpen}
              onClose={handleAccountMenuClose}
            >
              {isXSmallScreen && <ListItem>
                <ListItemIcon style={{ minWidth: 40 }}>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={profile.username} />
              </ListItem>}
              <ListItem>
                <ListItemIcon style={{ minWidth: 40 }}>
                  <LanguageIcon fontSize="small" />
                </ListItemIcon>
                <div style={{ marginLeft: -8 }}>
                  {LANGUAGES.map(language => (
                    <Button
                      color="secondary"
                      key={language}
                      variant={i18n.language === language ? 'contained' : 'text'}
                      className={classes.language}
                      onClick={() => i18n.changeLanguage(language)}
                    >
                      {language}
                    </Button>
                  ))}
                </div>
              </ListItem>
              <MenuItem
                onClick={() => {
                  api.logout();
                  history.push('/')
                }}
              >
                <ListItemIcon style={{ minWidth: 40 }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('logOut')} />
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <nav className={classes.drawer}>
        <Hidden smUp>
          <Drawer
            variant="temporary"
            open={temporaryDrawerOpen}
            classes={{ paper: classes.drawerPaper }}
            onClose={handleDrawerClose}
            ModalProps={{ keepMounted: true }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown>
          <Drawer
            variant="persistent"
            classes={{ paper: classes.drawerPaper }}
            open={persistentDrawerOpen}
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <main className={clsx(classes.content, {
        [classes.contentShift]: persistentDrawerOpen,
      })}>
        <div className={classes.toolbar} />
        <div className={classes.mainWrap}>{children}</div>
      </main>
    </div>
  )
}
export default MainNavigation
